import type { ConnectorConnection } from '@verybigthings/trackframe-shared/schemas';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ConfirmDialog';

// M16 task-08 — props-only view for Settings → Integrations (CONN-08;
// HUB-075 §4). Data fetching + `hub.*` calls live in the `Integrations`
// container (./Integrations.tsx); this component only renders props, so the
// admin's static-markup test harness (no jsdom) can pin every UI state —
// same split as pages/users/{Memory,memory-view}.

/** Copy shown on a `credential_invalid` connection (task-05 AC-3 reconnect flow). */
export const RECONNECT_WARNING = 'Reconnect to resume syncing.';

/** ConfirmDialog copy — disconnect wipes the tokens AND every synced doc (HUB-075 §3). */
export const DISCONNECT_WARNING =
  'Disconnecting removes the Google Drive connection and permanently deletes ALL documents it synced. This cannot be undone.';

/** Callback-landing banner (?connected= / ?error= from the consent flow). */
export interface CallbackBanner {
  kind: 'success' | 'error';
  text: string;
}

export interface IntegrationsViewProps {
  loading: boolean;
  loadError: string | null;
  /** The Drive connection, or null when nothing is connected. */
  connection: ConnectorConnection | null;
  banner: CallbackBanner | null;
  /** Connect / Reconnect — a FULL-PAGE navigation to the consent flow. */
  onConnect: () => void;
  folderInput: string;
  onFolderInputChange: (value: string) => void;
  onAddFolder: () => void;
  addFolderBusy: boolean;
  /** Inline message under the add-folder form (add/remove failures). */
  folderError: string | null;
  onRemoveFolder: (folderId: string) => void;
  removingFolderId: string | null;
  onSyncNow: () => void;
  syncBusy: boolean;
  /** Inline message on the connection card (sync/disconnect failures). */
  actionError: string | null;
  disconnectOpen: boolean;
  disconnectBusy: boolean;
  onRequestDisconnect: () => void;
  onConfirmDisconnect: () => void;
  onCancelDisconnect: () => void;
}

export function IntegrationsView({
  loading,
  loadError,
  connection,
  banner,
  onConnect,
  folderInput,
  onFolderInputChange,
  onAddFolder,
  addFolderBusy,
  folderError,
  onRemoveFolder,
  removingFolderId,
  onSyncNow,
  syncBusy,
  actionError,
  disconnectOpen,
  disconnectBusy,
  onRequestDisconnect,
  onConfirmDisconnect,
  onCancelDisconnect,
}: IntegrationsViewProps) {
  return (
    <div className="w-full space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Integrations</h1>
        <p className="text-sm text-muted-foreground">
          Connect external sources so your copilot can read from them.
        </p>
      </div>

      {banner &&
        (banner.kind === 'error' ? (
          <Alert>{banner.text}</Alert>
        ) : (
          <div
            role="status"
            className="rounded-md border border-transparent bg-success-soft px-3 py-2 text-sm text-success-soft-foreground"
          >
            {banner.text}
          </div>
        ))}

      {loadError && <Alert>{loadError}</Alert>}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : connection ? (
        <>
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between gap-3">
                <div className="space-y-1.5">
                  <CardTitle className="text-base">Google Drive</CardTitle>
                  <CardDescription>
                    Documents in your designated folders sync into your copilot&apos;s knowledge.
                  </CardDescription>
                </div>
                <Badge variant={connection.status === 'active' ? 'success' : 'destructive'}>
                  {connection.status === 'active' ? 'Active' : 'Credential invalid'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {connection.status === 'credential_invalid' && (
                <Alert variant="warning" className="flex items-center justify-between gap-3">
                  <span>{RECONNECT_WARNING}</span>
                  <Button type="button" size="sm" variant="outline" onClick={onConnect}>
                    Reconnect
                  </Button>
                </Alert>
              )}

              <div className="text-sm text-muted-foreground">
                {connection.lastSyncedAt
                  ? `Last synced ${new Date(connection.lastSyncedAt).toLocaleString()}`
                  : 'Not synced yet.'}
                {` · ${connection.docCount} synced document${connection.docCount === 1 ? '' : 's'}`}
              </div>

              {connection.lastSyncError && <Alert>Last sync error: {connection.lastSyncError}</Alert>}
              {actionError && <p className="text-sm text-destructive">{actionError}</p>}

              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSyncNow}
                  disabled={syncBusy || disconnectBusy}
                >
                  {syncBusy ? 'Queuing…' : 'Sync now'}
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={onRequestDisconnect}
                  disabled={syncBusy || disconnectBusy}
                >
                  Disconnect
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Designated folders</CardTitle>
              <CardDescription>
                Only documents inside these folders are synced.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form
                className="flex items-center gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  onAddFolder();
                }}
              >
                <Input
                  value={folderInput}
                  onChange={(e) => onFolderInputChange(e.target.value)}
                  placeholder="Paste a Drive folder link or ID"
                  aria-label="Drive folder link or ID"
                  disabled={addFolderBusy}
                />
                <Button type="submit" disabled={addFolderBusy || folderInput.trim() === ''}>
                  {addFolderBusy ? 'Adding…' : 'Add folder'}
                </Button>
              </form>

              {folderError && (
                <p role="alert" className="text-sm text-destructive">
                  {folderError}
                </p>
              )}

              {connection.folders.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No folders designated yet — nothing syncs until you add one.
                </p>
              ) : (
                <ul className="divide-y">
                  {connection.folders.map((folder) => (
                    <li key={folder.id} className="flex items-center justify-between gap-3 py-2">
                      <span className="text-sm">
                        {folder.name}
                        <span className="text-muted-foreground">
                          {` — ${folder.docCount} docs, ${folder.errorCount} errors`}
                        </span>
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        aria-label={`Remove folder ${folder.name}`}
                        onClick={() => onRemoveFolder(folder.id)}
                        disabled={removingFolderId !== null}
                      >
                        {removingFolderId === folder.id ? 'Removing…' : 'Remove'}
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <ConfirmDialog
            open={disconnectOpen}
            title="Disconnect Google Drive?"
            description={DISCONNECT_WARNING}
            confirmLabel="Disconnect"
            destructive
            busy={disconnectBusy}
            onConfirm={onConfirmDisconnect}
            onCancel={onCancelDisconnect}
          />
        </>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Google Drive</CardTitle>
            <CardDescription>
              Connect your Google Drive and designate folders to sync documents into your
              copilot&apos;s knowledge.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button type="button" onClick={onConnect}>
              Connect Google Drive
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
